/**
 * Wrapper de persistencia propio (sin librerías externas). IndexedDB como
 * almacén principal, con fallback automático a localStorage si IndexedDB no
 * está disponible o falla (navegadores antiguos, modo privado estricto,
 * cuota agotada, etc.), tal como pide docs/ARQUITECTURA.md.
 */

import type { SaveGame } from '@/data/types';

const DB_NAME = 'la-casa-db';
const DB_VERSION = 1;
const STORE_NAME = 'saves';
const RECORD_KEY = 'current';
const LOCALSTORAGE_KEY = 'la-casa-save-v1';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB no disponible'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Error abriendo IndexedDB'));
  });
}

async function idbLoad(): Promise<SaveGame | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(RECORD_KEY);
    req.onsuccess = () => resolve((req.result as SaveGame | undefined) ?? null);
    req.onerror = () => reject(req.error ?? new Error('Error leyendo save de IndexedDB'));
    tx.oncomplete = () => db.close();
  });
}

async function idbSave(save: SaveGame): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put(save, RECORD_KEY);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => reject(tx.error ?? new Error('Error escribiendo save en IndexedDB'));
    // Si la transacción se aborta (cuota, constraint, etc.) onerror puede no
    // dispararse siempre; cubrimos ese caso para que la promesa nunca quede
    // colgada.
    tx.onabort = () => reject(tx.error ?? new Error('Transacción abortada'));
  });
}

function localStorageLoad(): SaveGame | null {
  try {
    const raw = localStorage.getItem(LOCALSTORAGE_KEY);
    return raw ? (JSON.parse(raw) as SaveGame) : null;
  } catch (err) {
    console.warn('[storage] fallo leyendo localStorage', err);
    return null;
  }
}

function localStorageSave(save: SaveGame): void {
  try {
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(save));
  } catch (err) {
    console.warn('[storage] fallo escribiendo localStorage', err);
  }
}

/** Devuelve el save más reciente entre dos candidatos según su campo savedAt. */
function pickNewer(a: SaveGame | null, b: SaveGame | null): SaveGame | null {
  if (a && b) {
    const aTime = a.savedAt ?? 0;
    const bTime = b.savedAt ?? 0;
    return aTime >= bTime ? a : b;
  }
  return a ?? b;
}

/**
 * Carga el save. Espejo IDB/localStorage: lee ambos y se queda con el más
 * nuevo comparando `savedAt`. Si solo existe uno, usa ese. Si ambos fallan,
 * devuelve null.
 */
export async function loadSave(): Promise<SaveGame | null> {
  const [idbResult, lsResult] = await Promise.all([
    idbLoad().catch((err) => {
      console.warn('[storage] fallo leyendo IndexedDB', err);
      return null;
    }),
    Promise.resolve(localStorageLoad()),
  ]);
  return pickNewer(idbResult, lsResult);
}

/**
 * Persiste el save. Espejo IDB/localStorage: estampa `savedAt` y escribe
 * SIEMPRE en ambos. Si uno falla se avisa pero no se aborta el otro (la
 * redundancia es la gracia del espejo).
 */
export async function persistSave(save: SaveGame): Promise<void> {
  const stamped: SaveGame = { ...save, savedAt: Date.now() };
  // localStorage primero: es síncrono y no puede colgarse, así siempre queda
  // al menos una copia aunque IndexedDB se atasque.
  localStorageSave(stamped);
  await idbSave(stamped).catch((err) => {
    console.warn('[storage] fallo escribiendo en IndexedDB (queda el espejo de localStorage)', err);
  });
}

/** Exporta el save actual como JSON descargable (respaldo de la beta). */
export function exportSaveAsJson(save: SaveGame): string {
  return JSON.stringify(save, null, 2);
}

/**
 * Importa un save desde una cadena JSON (contraparte de exportSaveAsJson).
 * Valida la forma mínima esperada; devuelve null si el JSON es inválido o no
 * tiene la estructura de un SaveGame. Se cableará a la UI en bloques futuros.
 */
export function importSaveFromJson(raw: string): SaveGame | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as Record<string, unknown>).version === 'number' &&
      typeof (parsed as Record<string, unknown>).respeto === 'number' &&
      typeof (parsed as Record<string, unknown>).dinero === 'number' &&
      typeof (parsed as Record<string, unknown>).playableCharacterId === 'string' &&
      typeof (parsed as Record<string, unknown>).flags === 'object' &&
      (parsed as Record<string, unknown>).flags !== null
    ) {
      return parsed as SaveGame;
    }
    return null;
  } catch (err) {
    console.warn('[storage] JSON inválido al importar save', err);
    return null;
  }
}
